import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import LocationCard from "./FormComponents/LocationCard";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditMakatiGpcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [sendEmailAfterEdit, setSendEmailAfterEdit] = useState(true);

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  // Function to recursively convert true/false to "True"/"False" in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False";
    }
    return obj;
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (type === 'checkbox') {
      setJobOrder(prev => ({ ...prev, [name]: checked }));
    } else {
      setJobOrder(prev => ({ ...prev, [name]: value }));
    }
  };


  // Handle nested object changes
  const handleNestedChange = (parentKey, childKey, value) => {
    setJobOrder(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  // Handle array item changes
  const handleArrayItemChange = (arrayKey, index, field, value) => {
    setJobOrder(prev => ({
      ...prev,
      [arrayKey]: prev[arrayKey]?.map((item, idx) => 
        idx === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };

  const buildClientSignatureIsoFromEditedTime = (hhmm) => {
    if (!hhmm) return jobOrder?.client_signature_uploaded_at || "";

    const [hour, minute] = String(hhmm).split(":").map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return jobOrder?.client_signature_uploaded_at || "";
    }

    try {
      const baseIso = jobOrder?.client_signature_uploaded_at;
      const base = baseIso
        ? new Date(baseIso)
        : new Date(`${jobOrder?.job_date}T00:00:00+08:00`);

      if (Number.isNaN(base.getTime())) {
        return jobOrder?.client_signature_uploaded_at || "";
      }

      const year = base.getUTCFullYear();
      const month = base.getUTCMonth();
      const date = base.getUTCDate();

      const rebuilt = new Date(Date.UTC(year, month, date, hour - 8, minute, 0));
      return rebuilt.toISOString();
    } catch {
      return jobOrder?.client_signature_uploaded_at || "";
    }
  };

  // Save job order function
  const handleSave = async () => {
    try {
      setIsLoading(true);

      // 1) Rebuild inspection report exactly like create form
      const inspection_report = buildInspectionReport();

      // 2) Compose the parameters to reflect current UI state
      const updatedJobOrderParameters = {
        ...jobOrder,
        inspection_report,
        service_report_payment_status,
        service_report_payment_method,
        end_time: jobOrder.end_time || "",
        ...(usesClientSignatureAsEndTime
          ? {
              client_signature_uploaded_at:
                jobOrder.client_signature_uploaded_at || "",
            }
          : {}),
      };

      // 3) Prepare the mapper payload (convert booleans to "True"/"False")
      const payload = convertBooleans({
        jobOrder,                        // base job order
        updatedJobOrderParameters,       // the edited parameters you want to render to PDFs
        form: updatedJobOrderParameters,                  // reuse jobOrder here to match the shape from MakatiGpcForm
      });

      // 4) Regenerate PDFs (Report, Certificate, Receipt)
      await submitDocumentFieldMapper(payload);

      // 5) Compute the new S3 links (same convention as create form)
      const newLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: true,
        includeCertificate: true,
        includeReceipt: true,
      });

      // 6) Persist links to backend & update local state so the UI shows the latest
      await patchJobOrderLink(jobOrder.id, newLinks);
      await putJobOrder(jobOrder.id, {
        ...updatedJobOrderParameters,
        ...newLinks,
      });

      setJobOrder((prev) => ({
        ...prev,
        ...updatedJobOrderParameters,
        ...newLinks,
      }));

      if (sendEmailAfterEdit) {
        await handleSendEmail(newLinks);
        setSuccessMessage(
          "Service report updated, documents regenerated, and email sent successfully!"
        );
      } else {
        setSuccessMessage(
          "Service report updated and documents regenerated successfully."
        );
      }
    } catch (err) {
      setErrorMessage("Error saving job order: " + (err.message || err));
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage("Error fetching job order: " + (err.message || err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]); // Dependency array ensures the effect runs when ID changes

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  // ---------------Inspection Report
  const PEST_OPTIONS = useMemo(() => ["Roaches", "Mosq", "Rodents", "Flies", "Ants", "Others"], []);

  const FINDING_OPTIONS = useMemo(() => [
    { value: "", label: "None" },
    { value: "Presence of foul odor", label: "Presence of foul odor" },
    { value: "Trash bins left uncovered", label: "Trash bins left uncovered" },
    { value: "Dirt/Organic Matter Present", label: "Dirt/Organic Matter Present" },
    { value: "Cracks and Crevices", label: "Cracks and Crevices" },
    { value: "Pest Entry Point / Open Windows", label: "Pest Entry Point / Open Windows" },
    { value: "Stagnant Water / Leaking Pipes", label: "Stagnant Water / Leaking Pipes" },
    { value: "Build-up of Old Stocks", label: "Build-up of Old Stocks" },
    { value: "Closed Room or Area Inaccessible", label: "Closed Room or Area Inaccessible" },
    { value: "Active termites found", label: "Active termites found" },
  ], []);

  const RECOMMENDATION_OPTIONS = useMemo(() => [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
  ], []);

  // Normalize pests into a fixed object shape with numeric values
  const normalizePests = useCallback((p) => {
    // base with all six keys = 0
    const base = PEST_OPTIONS.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});

    if (Array.isArray(p)) {
      // OLD shape: [{ type, count }]
      for (const { type, count } of p) {
        if (type && Object.prototype.hasOwnProperty.call(base, type)) {
          base[type] = Number(count) || 0;
        }
      }
    } else if (p && typeof p === "object") {
      // NEW shape: { Roaches: 0, Mosq: 0, ... }
      for (const [k, v] of Object.entries(p)) {
        if (Object.prototype.hasOwnProperty.call(base, k)) {
          base[k] = Number(v) || 0;
        }
      }
    }
    return base;
  }, [PEST_OPTIONS]);

  // Return { select: valueForSelect, other: valueForOtherTextField }
  const coerceOtherSelect = useCallback((raw, OPTIONS) => {
    const v = (raw ?? "").trim();
    if (!v) return { select: "", other: "" };

    // options may be strings or { value, label }
    const values = OPTIONS.map(o => (typeof o === "string" ? o : o.value));
    const labels = OPTIONS.map(o => (typeof o === "string" ? o : o.label));

    // case-insensitive match for robustness
    const matches = (list) =>
      list.find(opt => opt?.localeCompare(v, undefined, { sensitivity: "accent" }) === 0);

    if (matches(values) || matches(labels)) {
      // It's a known option; use it directly as the select value
      return { select: matches(values) || matches(labels), other: "" };
    }

    // Not a known option → select "Other", put original in *_other
    return { select: "Other", other: v };
  }, []);

  const { control, register } = useForm({
   defaultValues: {
     locations: [
       {
         location: "",
         pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
         findings: "",
         findings_other: "",
         recommendation: "",
         recommendation_other: "",
       },
     ],
   },
  });

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
    replace: replaceLocations,
  } = useFieldArray({ control, name: "locations" });

  // watch the entire locations array
  const allLocations = useWatch({ control, name: "locations" }) || [];

  // initialize totals
  const overallTotals = PEST_OPTIONS.reduce((acc, pest) => {
    acc[pest] = 0;
    return acc;
  }, {});

  // sum each location’s pests (supports array OR object)
  allLocations.forEach((loc) => {
    const np = normalizePests(loc?.pests);
    PEST_OPTIONS.forEach((k) => {
      overallTotals[k] += np[k];
    });
  });

  // grand total
  const grandTotal = Object.values(overallTotals).reduce((a, b) => a + b, 0);

  // Consider an inspection row "blank" if location/findings/recommendation are empty AND all pest counts are zero.
  const isBlankInspectionRow = useCallback((row) => {
    const loc = (row?.location || "").trim();
    const findings = (row?.findings || "").trim();
    const recommendation = (row?.recommendation || "").trim();
    const pests = normalizePests(row?.pests);
    const allZero = Object.values(pests).every((n) => Number(n) === 0);
    return !loc && !findings && !recommendation && allZero;
  }, [normalizePests]);

  useEffect(() => {
    const ir = jobOrder?.inspection_report;
    if (!Array.isArray(ir)) return;

    // Map incoming IR -> RHF locations shape
    const mapped = ir
    .slice(0, 15)
    .filter((row) => !isBlankInspectionRow(row)) // ⬅️ filter out empty rows
    .map((row) => {
      const pests = normalizePests(row?.pests); // uses your existing helper

     // Coerce findings/recommendation to "Other" if not in options
     const f = coerceOtherSelect(row?.findings, FINDING_OPTIONS);
     const r = coerceOtherSelect(row?.recommendation, RECOMMENDATION_OPTIONS);

      return {
        location: row?.location || "",
        pests,                                 // { Roaches, Mosq, Rodents, Flies, Ants, Others } as numbers
        findings: f.select,
        findings_other: f.other,
        recommendation: r.select,
        recommendation_other: r.other,
      };
    });

    // If the API sometimes returns an empty array, skip replacing to keep defaults
    if (mapped.length) replaceLocations(mapped);
  }, [jobOrder?.inspection_report, replaceLocations, isBlankInspectionRow, normalizePests, coerceOtherSelect, FINDING_OPTIONS, RECOMMENDATION_OPTIONS]);

  // Build the inspection_report array (exactly 15 rows, like create form)
  const buildInspectionReport = () => {
    return Array.from({ length: 15 }).map((_, idx) => {
      const loc = (allLocations && allLocations[idx]) || {
        location: "",
        pests: {},
        findings: "",
        findings_other: "",
        recommendation: "",
        recommendation_other: "",
      };

      // Prefer "*_other" value when present
      const findings = (loc.findings_other?.trim() || loc.findings || "").trim();
      const recommendation = (loc.recommendation_other?.trim() || loc.recommendation || "").trim();

      // Ensure pest object has all keys and numbers
      const pests = normalizePests(loc.pests);

      // String total, like the API expects
      const total = PEST_OPTIONS.reduce((sum, k) => sum + pests[k], 0);

      return {
        location: loc.location || "",
        pests,
        total: String(total),
        findings,
        recommendation,
      };
    });
  };


  // --- Payment defaults (keep the order stable)
  const DEFAULT_PAYMENT_STATUS = useMemo(() => [
    { label: "Paid",          selection: false },
    { label: "COD",           selection: false },
    { label: "For Collection",selection: false },
  ], []);

  const DEFAULT_PAYMENT_METHOD = useMemo(() => [
    { label: "Cash",  selection: false },
    { label: "Check", selection: false },
    { label: "Free",  selection: false },
  ], []);

  // Local UI state (what the table renders)
  const [service_report_payment_status, setServiceReportPaymentStatus] = useState(DEFAULT_PAYMENT_STATUS);
  const [service_report_payment_method, setServiceReportPaymentMethod]   = useState(DEFAULT_PAYMENT_METHOD);

  // Normalize incoming array/string → align with our defaults by label
  const normalizeByLabel = useCallback((incoming, defaults) => {
    if (Array.isArray(incoming)) {
      const map = new Map(incoming.map(i => [String(i.label || i.value).toLowerCase(), !!i.selection]));
      return defaults.map(d => ({ ...d, selection: !!map.get(d.label.toLowerCase()) }));
    }
    if (typeof incoming === "string") {
      const v = incoming.toLowerCase();
      return defaults.map(d => ({ ...d, selection: d.label.toLowerCase() === v }));
    }
    return defaults;
  }, []);

  useEffect(() => {
    // Pre-fill the radio-like checkboxes from the payload if present
    const status = normalizeByLabel(jobOrder?.service_report_payment_status, DEFAULT_PAYMENT_STATUS);
    const method = normalizeByLabel(jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_METHOD);

    setServiceReportPaymentStatus(status);
    setServiceReportPaymentMethod(method);

    // Keep the outgoing payload in sync as well
    setJobOrder(prev => ({
      ...prev,
      service_report_payment_status: status,
      service_report_payment_method: method,
    }));
  }, [jobOrder?.service_report_payment_status, jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_STATUS, DEFAULT_PAYMENT_METHOD, normalizeByLabel]);

  const handlePaymentStatusChange = (statusIndex) => {
    const next = service_report_payment_status.map((s, i) => ({
      ...s,
      selection: i === statusIndex ? !s.selection : false,
    }));
    setServiceReportPaymentStatus(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_status: next }));
  };

  const handlePaymentMethodChange = (methodIndex) => {
    const next = service_report_payment_method.map((m, i) => ({
      ...m,
      selection: i === methodIndex ? !m.selection : false,
    }));
    setServiceReportPaymentMethod(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_method: next }));
  };

  // ------ Send Email
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);
      if (isNaN(date.getTime())) throw new Error("Invalid date format");
      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch {
      return "Invalid Date";
    }
  }
  const formattedDate = formatDate(jobOrder?.job_date);

  const handleSendEmail = async (attachmentLinks) => {
    const recipientEmail =
      jobOrder?.client?.contact?.email?.email ||
      client?.contact?.email?.email ||
      "";

    if (!recipientEmail) {
      setErrorMessage("No client email found for this job order.");
      return;
    }

    const htmlBody = buildServiceReportEmail({
      formattedDate,
      attachmentLinks,
    });

    const lambdaPayload = {
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [recipientEmail],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      await sendEmailQueue(lambdaPayload);
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage("Failed to send email.");
    }
  };

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const usesClientSignatureAsEndTime = Boolean(jobOrder?.client_signature_uploaded_at);

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    inspection_report: "Inspection Report Completed At",
    service_details: "Service Details Completed At",
    payment_details: "Payment Details Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) =>
    jobOrder?.[`${stepKey}_completed_at`] || latestStepLogMap?.[stepKey] || "";

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: true,
      includeCertificate: true,
      includeReceipt: true,
    }),
    ...(jobOrder?.reports_url && {
      reports_url: jobOrder.reports_url,
    }),
    ...(jobOrder?.certificate_url && {
      certificate_url: jobOrder.certificate_url,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>      
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={client}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              status: value,
            }))
          }
          showStatus={true}
          readOnly={false}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Inspection & Pest Count (Mobile) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Inspection Report
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Stack spacing={2}>
                {locationFields.map((loc, idx) => (
                  <LocationCard
                    key={loc.id}
                    control={control}
                    register={register}
                    index={idx}
                    removeLocation={removeLocation}
                  />
                ))}

                <Button
                  variant="contained"
                  disabled={locationFields.length >= 15}
                  fullWidth
                  onClick={() =>
                    appendLocation({
                      location: "",
                      // pests: [{ type: "", count: "" }],
                      pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
                      findings: "",
                      findings_other: "",
                      recommendation: "",
                      recommendation_other: "",
                    })
                  }
                >
                  Add New Location ({locationFields.length}/15)
                </Button>

                {/* Overall Total Pests Section */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                        >
                          OVERALL TOTAL PESTS
                        </Typography>
                      }
                      sx={{ pb: 0, px: 2, pt: 2 }}
                    />
                    <CardContent sx={{ px: 2, pb: 2 }}>
                      {PEST_OPTIONS.map((pest) => (
                        <Typography
                          key={pest}
                          variant="body2"
                          sx={{ display: "flex", justifyContent: "space-between" }}
                        >
                          {pest}
                          <span>{overallTotals[pest] || 0}</span>
                        </Typography>
                      ))}

                      <Typography
                        variant="subtitle1"
                        sx={{ mt: 1, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}
                      >
                        Total
                        <span>{grandTotal}</span>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.inspection_report}
              value={getStepTimestamp("inspection_report")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Activities Done */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Activities Done</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <FormGroup>
                {(jobOrder.services || []).map((service, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={service.activityChecked || false}
                        onChange={(e) => {
                          const newServices = [...(jobOrder.services || [])];
                          newServices[idx] = {
                            ...newServices[idx],
                            activityChecked: e.target.checked
                          };
                          setJobOrder(prev => ({
                            ...prev,
                            services: newServices
                          }));
                        }}
                      />
                    }
                    label={service.activity?.replace(/_/g, ' ') || ''}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Services Performed */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Services Performed</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <FormGroup>
                {(jobOrder.services || []).map((service, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={service.serviceChecked || false}
                        onChange={(e) => {
                          const newServices = [...(jobOrder.services || [])];
                          newServices[idx] = {
                            ...newServices[idx],
                            serviceChecked: e.target.checked
                          };
                          setJobOrder(prev => ({
                            ...prev,
                            services: newServices
                          }));
                        }}
                      />
                    }
                    label={service.service?.replace(/_/g, ' ') || ''}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Notes Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Reminder Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Reminder Notes"
                name="reminder_notes"
                value={jobOrder.reminder_notes || ""}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={6}
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Chemical Used Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Chemical Used / Batch # / Expiration
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: "bold" }}
                        >
                          Dilution
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment Used
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(jobOrder.chemical_used || []).map((chem, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{chem.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.chemical || ""}
                              onChange={(e) => handleArrayItemChange('chemical_used', idx, 'chemical', e.target.value)}
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.dilution || ""}
                              onChange={(e) => handleArrayItemChange('chemical_used', idx, 'dilution', e.target.value)}
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          {jobOrder.equipment_used?.length > idx ? (
                            <>
                              <TableCell>
                                {jobOrder.equipment_used[idx].label}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={jobOrder.equipment_used[idx].usage}
                                  onChange={(e) => handleArrayItemChange('equipment_used', idx, 'usage', e.target.checked)}
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell />
                              <TableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Against Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  CONTROL AGAINST
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.crawling_insects || false}
                      onChange={(e) => handleNestedChange('control_against', 'crawling_insects', e.target.checked)}
                    />
                  }
                  label="Crawling Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.flying_insects || false}
                      onChange={(e) => handleNestedChange('control_against', 'flying_insects', e.target.checked)}
                    />
                  }
                  label="Flying Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.rodent_control || false}
                      onChange={(e) => handleNestedChange('control_against', 'rodent_control', e.target.checked)}
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.others || false}
                      onChange={(e) => handleNestedChange('control_against', 'others', e.target.checked)}
                    />
                  }
                  label="Others"
                />
              </FormGroup>

              {jobOrder.control_against?.others && (
                <TextField
                  label="Please specify"
                  value={jobOrder.control_against?.others_text || ""}
                  onChange={(e) => handleNestedChange('control_against', 'others_text', e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.service_details}
              value={getStepTimestamp("service_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Payment Status Section */}
        <PaymentDetailsSection
          serviceFee={jobOrder?.service_fee || ""}
          onServiceFeeChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              service_fee: value,
            }))
          }
          paymentStatusOptions={service_report_payment_status}
          paymentMethodOptions={service_report_payment_method}
          onPaymentStatusChange={handlePaymentStatusChange}
          onPaymentMethodChange={handlePaymentMethodChange}
          readOnly={false}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.payment_details}
              value={getStepTimestamp("payment_details")}
              colorSet="info"
            />
          </Grid>
        )}
                
        {/* Technician Signature Section */}
        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
          editableSchedule={true}
          onStartTimeChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              start_time: value,
            }))
          }
          onEndTimeChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              end_time: value, // display/edit field
              ...(usesClientSignatureAsEndTime
                ? {
                    client_signature_uploaded_at:
                      buildClientSignatureIsoFromEditedTime(value),
                  }
                : {}),
            }))
          }
        />
      
        {/* Client Signature Section */}
        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {/* Document Links Section */}
        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        {/* Action Buttons Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        >
          {/* Checkbox moved to the right side near actions */}
          <FormControlLabel
            sx={{
              mr: 2,
              ".MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
            }}
            control={
              <Checkbox
                checked={sendEmailAfterEdit}
                onChange={(e) => setSendEmailAfterEdit(e.target.checked)}
                disabled={isLoading}
              />
            }
            label="Send email after save"
          />

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
            disabled={isLoading}
          >
            {sendEmailAfterEdit ? "Save & Send Email" : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default EditMakatiGpcForm;
